import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { 
  MessageSquare, Send, Sparkles, User, Bot, Trash2, 
  Plus, History, X, Loader2, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIChatHistory = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load sessions when dialog opens
  useEffect(() => {
    if (open) {
      fetchSessions();
    }
  }, [open]);

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const response = await axios.get(`${API_URL}/ai/chat/sessions`);
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      // Initialize with empty sessions
      setSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const response = await axios.get(`${API_URL}/ai/chat/sessions/${sessionId}`);
      setCurrentSession(response.data);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load session:', error);
      toast.error('Failed to load chat session');
    }
  };

  const createNewSession = async () => {
    try {
      const response = await axios.post(`${API_URL}/ai/chat/sessions`);
      const newSession = response.data;
      setSessions(prev => [newSession, ...prev]);
      setCurrentSession(newSession);
      setMessages([]);
      toast.success('New chat session created');
    } catch (error) {
      console.error('Failed to create session:', error);
      toast.error('Failed to create new session');
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API_URL}/ai/chat/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (currentSession?.id === sessionId) {
        setCurrentSession(null);
        setMessages([]);
      }
      toast.success('Chat session deleted');
    } catch (error) {
      console.error('Failed to delete session:', error);
      toast.error('Failed to delete session');
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/ai/chat/message`, {
        session_id: currentSession?.id,
        message: input
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Update session if new one was created
      if (response.data.session_id && !currentSession) {
        setCurrentSession({ id: response.data.session_id });
        fetchSessions();
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to get AI response');
      // Remove the user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const suggestedQuestions = [
    "What are my top 3 cost optimization opportunities?",
    "Explain the recent cost spike in compute services",
    "How can I reduce my AWS EC2 spending?",
    "What's driving my month-over-month cost increase?",
    "Which services have the most optimization potential?"
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2 border-cyan-200 text-cyan-700 hover:bg-cyan-50 dark:border-cyan-800 dark:text-cyan-400"
          data-testid="ai-chat-btn"
        >
          <MessageSquare className="w-4 h-4" />
          <span className="hidden sm:inline">AI Chat</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl h-[80vh] p-0 gap-0" data-testid="ai-chat-dialog">
        <div className="flex h-full">
          {/* Sidebar - Chat Sessions */}
          <div className="w-64 border-r border-slate-200 dark:border-slate-700 flex flex-col bg-slate-50 dark:bg-slate-900">
            <div className="p-3 border-b border-slate-200 dark:border-slate-700">
              <Button 
                onClick={createNewSession}
                className="w-full gap-2 bg-cyan-600 hover:bg-cyan-700"
                size="sm"
              >
                <Plus className="w-4 h-4" />
                New Chat
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {loadingSessions ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : sessions.length === 0 ? (
                  <div className="text-center py-4 text-sm text-slate-400">
                    No chat history yet
                  </div>
                ) : (
                  sessions.map((session) => (
                    <div
                      key={session.id}
                      className={`group p-2 rounded-lg cursor-pointer transition-colors flex items-center justify-between ${
                        currentSession?.id === session.id
                          ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400'
                          : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                      onClick={() => loadSession(session.id)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {session.title || 'New Chat'}
                        </p>
                        <p className="text-xs text-slate-400 truncate">
                          {formatTimestamp(session.created_at)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSession(session.id);
                        }}
                      >
                        <Trash2 className="w-3 h-3 text-slate-400 hover:text-rose-500" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Main Chat Area */}
          <div className="flex-1 flex flex-col">
            <DialogHeader className="p-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-600" />
                <DialogTitle>AI Cost Advisor</DialogTitle>
                <Badge className="bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-400 text-[11px]">
                  Claude 4.5
                </Badge>
              </div>
            </DialogHeader>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="p-4 rounded-full bg-cyan-100 dark:bg-cyan-900/30 mb-4">
                    <Bot className="w-8 h-8 text-cyan-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Start a conversation</h3>
                  <p className="text-sm text-slate-400 mb-6 max-w-md">
                    Ask me anything about your cloud costs. I can help you understand spending patterns, 
                    identify optimization opportunities, and explain anomalies.
                  </p>
                  <div className="space-y-2 w-full max-w-md">
                    <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Suggested questions</p>
                    {suggestedQuestions.slice(0, 3).map((q, i) => (
                      <button
                        key={i}
                        onClick={() => setInput(q)}
                        className="w-full p-3 text-left text-sm rounded-lg border border-slate-200 dark:border-slate-700 hover:border-cyan-300 dark:hover:border-cyan-700 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition-colors flex items-center justify-between group"
                      >
                        <span>{q}</span>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-cyan-500" />
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-100 dark:bg-cyan-900/50 flex items-center justify-center">
                          <Bot className="w-4 h-4 text-cyan-600" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] p-3 rounded-lg ${
                          msg.role === 'user'
                            ? 'bg-cyan-600 text-white'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
                        }`}
                      >
                        <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                        <div className={`text-[11px] mt-1 ${
                          msg.role === 'user' ? 'text-cyan-200' : 'text-slate-400'
                        }`}>
                          {formatTimestamp(msg.timestamp)}
                        </div>
                      </div>
                      {msg.role === 'user' && (
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                          <User className="w-4 h-4 text-emerald-600" />
                        </div>
                      )}
                    </div>
                  ))}
                  {loading && (
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-100 dark:bg-cyan-900/50 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-cyan-600" />
                      </div>
                      <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-cyan-600" />
                          <span className="text-sm text-slate-400">Analyzing your costs...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Input */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-700">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about your cloud costs..."
                  disabled={loading}
                  className="flex-1"
                  data-testid="ai-chat-input"
                />
                <Button 
                  onClick={sendMessage}
                  disabled={!input.trim() || loading}
                  className="bg-cyan-600 hover:bg-cyan-700"
                  data-testid="ai-chat-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIChatHistory;
